import React from 'react';
import type { CampaignStatusCounts } from '../services/tasks';

/**
 * `detailed` — one segment per status, for admins and reviewers.
 * `simple`   — two segments (worked vs. still available), for experts, who
 *              only need to know how much of the campaign is left to claim.
 */
export type CampaignProgressBarVariant = 'detailed' | 'simple';

/**
 * `full`    — every status gets a legend entry, including the zeroes, so the
 *             admin tables line up column-to-column between campaigns.
 * `nonzero` — only statuses with tasks in them. Used on phones, where six
 *             legend entries wrap into an unreadable block.
 */
export type CampaignProgressBarLegend = 'full' | 'nonzero';

interface CampaignProgressBarProps {
  counts: CampaignStatusCounts | null;
  loading: boolean;
  variant?: CampaignProgressBarVariant;
  legend?: CampaignProgressBarLegend;
}

/**
 * Stacked bar + legend that summarizes a campaign's task pool by status.
 * Rendered both in the list row (small) and on the campaign detail page
 * (overall + per-type). Status grouping mirrors `userStats.ts`:
 *   - completed:  approved
 *   - in review:  in_review
 *   - rework:     changes_requested (broken out so admins see how many
 *                 tasks bounced back from review)
 *   - in progress: in_progress
 *   - claimed:    claimed
 *   - archived:   archived
 *
 * The `simple` variant collapses every non-available status into a single
 * "Complete / in progress" bucket — an expert can't act on the difference
 * between someone else's claimed task and someone else's approved one.
 */
export function CampaignProgressBar({
  counts,
  loading,
  variant = 'detailed',
  legend = 'full',
}: CampaignProgressBarProps) {
  if (!counts) {
    return <div className="mt-2 h-2 w-full rounded-full bg-gray-100" aria-hidden={!loading} />;
  }
  if (counts.total === 0) {
    return (
      <div className="mt-2 flex items-center gap-2">
        <div className="h-2 flex-1 rounded-full bg-gray-100" />
        <span className="text-[11px] text-gray-400 whitespace-nowrap">No tasks</span>
      </div>
    );
  }
  if (variant === 'simple') return <SimpleProgressBar counts={counts} />;
  const segments = [
    { key: 'approved', label: 'Completed', count: counts.approved, color: 'bg-emerald-500' },
    { key: 'inReview', label: 'In review', count: counts.inReview, color: 'bg-blue-500' },
    { key: 'rework', label: 'Rework', count: counts.rework, color: 'bg-rose-500' },
    { key: 'inProgress', label: 'In progress', count: counts.inProgress, color: 'bg-violet-500' },
    { key: 'claimed', label: 'Claimed', count: counts.claimed, color: 'bg-amber-500' },
    { key: 'archived', label: 'Archived', count: counts.archived, color: 'bg-gray-700' },
  ];
  const tooltip = [
    `${counts.total} created`,
    `${counts.approved} completed`,
    `${counts.inReview} in review`,
    `${counts.rework} rework`,
    `${counts.inProgress} in progress`,
    `${counts.claimed} claimed`,
    `${counts.available} available`,
    `${counts.archived} archived`,
  ].join(' · ');
  return (
    <div className="mt-2">
      <div
        className="flex h-2 w-full overflow-hidden rounded-full bg-gray-100"
        title={tooltip}
        role="img"
        aria-label={tooltip}
      >
        {segments.map((s) =>
          s.count > 0 ? (
            <div
              key={s.key}
              className={s.color}
              style={{ width: `${(s.count / counts.total) * 100}%` }}
            />
          ) : null,
        )}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-gray-500">
        <span className="font-medium text-gray-600">{counts.total} created</span>
        {segments
          .filter((s) => legend === 'full' || s.count > 0)
          .map((s) => (
            <span key={s.key} className="flex items-center gap-1">
              <span className={`h-2 w-2 rounded-full ${s.color}`} />
              {s.count} {s.label.toLowerCase()}
            </span>
          ))}
        {(legend === 'full' || counts.available > 0) && (
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-gray-200" />
            {counts.available} available
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Expert-facing two-segment bar: everything that has been picked up (claimed,
 * in progress, in review, rework, approved, archived) against what is still
 * sitting in the available pool.
 */
function SimpleProgressBar({ counts }: { counts: CampaignStatusCounts }) {
  const worked = counts.total - counts.available;
  const tooltip = [
    `${counts.total} created`,
    `${worked} complete or in progress`,
    `${counts.available} available`,
  ].join(' · ');
  return (
    <div className="mt-2">
      <div
        className="flex h-2 w-full overflow-hidden rounded-full bg-gray-100"
        title={tooltip}
        role="img"
        aria-label={tooltip}
      >
        {worked > 0 && (
          <div className="bg-emerald-500" style={{ width: `${(worked / counts.total) * 100}%` }} />
        )}
        {counts.available > 0 && (
          <div
            className="bg-gray-300"
            style={{ width: `${(counts.available / counts.total) * 100}%` }}
          />
        )}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-gray-500">
        <span className="font-medium text-gray-600">{counts.total} created</span>
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          {worked} complete / in progress
        </span>
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-gray-300" />
          {counts.available} available
        </span>
      </div>
    </div>
  );
}
